import { dateTimeFormat, formatNumber, formatUsd, numberFormat } from '../../lib/format';
import { DashboardMetric, type DashboardDateRange } from './model';

export function formatDashboardMetric(value: number | undefined, metric: DashboardMetric) {
  if (value === undefined) return '-';
  if (metric === DashboardMetric.FillRate) return formatFillRatePercentage(value * 100);
  if (isDashboardCurrencyMetric(metric)) {
    return numberFormat({
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: metric === DashboardMetric.Rpm ? 3 : 2,
    }).format(value);
  }
  return numberFormat({ notation: 'compact', maximumFractionDigits: 1 }).format(value);
}

export function formatDashboardChartValue(value: number, metric: DashboardMetric) {
  if (metric === DashboardMetric.FillRate) return formatFillRatePercentage(value);
  if (isDashboardCurrencyMetric(metric)) {
    const compact = Math.abs(value) >= 1_000;
    const maximumFractionDigits = metric === DashboardMetric.Rpm || Math.abs(value) < 1 ? 3 : 2;
    return numberFormat({
      style: 'currency',
      currency: 'USD',
      notation: compact ? 'compact' : 'standard',
      minimumFractionDigits: compact ? 0 : 2,
      maximumFractionDigits,
    }).format(value);
  }
  return numberFormat({ notation: 'compact', maximumFractionDigits: 1 }).format(value);
}

function formatFillRatePercentage(value: number) {
  // Keep small nonzero rates visible without padding normal rates with trailing zeros.
  const formatted =
    Math.abs(value) < 1
      ? numberFormat({ maximumSignificantDigits: 3 }).format(value)
      : formatNumber(value, 1);
  return `${formatted}%`;
}

function isDashboardCurrencyMetric(metric: DashboardMetric) {
  return (
    metric === DashboardMetric.Revenue ||
    metric === DashboardMetric.PublisherRevenue ||
    metric === DashboardMetric.Profit ||
    metric === DashboardMetric.NetworkRevenue ||
    metric === DashboardMetric.SuperAdminProfit ||
    metric === DashboardMetric.Rpm ||
    metric === DashboardMetric.Srpm
  );
}

export function dashboardSrpm(
  revenue: number | null | undefined,
  requests: number | null | undefined,
) {
  if (revenue === null || revenue === undefined || !requests || requests <= 0) return undefined;
  return (revenue / requests) * 1_000_000;
}

export function formatSrpm(value: number | null | undefined) {
  return formatUsd(value, {
    minimumFractionDigits: 2,
    maximumFractionDigits: value !== null && value !== undefined && Math.abs(value) < 1 ? 4 : 2,
  });
}

export function dashboardMetricLabelKey(metric: DashboardMetric) {
  return (
    {
      [DashboardMetric.AdClicks]: 'dashboard.metric.adClicks',
      [DashboardMetric.AdRequests]: 'dashboard.metric.adRequests',
      [DashboardMetric.AdImpressions]: 'dashboard.metric.adImpressions',
      [DashboardMetric.FillRate]: 'dashboard.metric.fillRate',
      [DashboardMetric.Rpm]: 'dashboard.metric.rpm',
      [DashboardMetric.Srpm]: 'dashboard.metric.srpm',
      [DashboardMetric.Revenue]: 'dashboard.metric.revenue',
      [DashboardMetric.PublisherRevenue]: 'dashboard.metric.publisherRevenue',
      [DashboardMetric.Profit]: 'dashboard.metric.profit',
      [DashboardMetric.NetworkRevenue]: 'dashboard.metric.networkRevenue',
      [DashboardMetric.SuperAdminProfit]: 'dashboard.metric.profit',
    } as const
  )[metric];
}

export function formatDashboardPreviousPeriod(
  range: DashboardDateRange,
  previousPeriodEnd?: string | null,
) {
  const from = parseUtcDashboardDate(range.dateFrom);
  const to = parseUtcDashboardDate(range.dateTo);
  if (!from || !to || from > to) return null;

  const periodDays = Math.floor((to.getTime() - from.getTime()) / dayMilliseconds) + 1;
  const previousTo = new Date(from.getTime() - dayMilliseconds);
  const previousFrom = new Date(previousTo.getTime() - (periodDays - 1) * dayMilliseconds);
  const label = formatPreviousDateRange(previousFrom, previousTo, from.getUTCFullYear());
  if (previousPeriodEnd && periodDays === 1) {
    const end = new Date(previousPeriodEnd);
    const hours = (end.getTime() - previousFrom.getTime()) / (60 * 60 * 1000);
    if (Number.isInteger(hours) && hours >= 0 && hours <= 24) {
      return `${label}, 00h–${String(hours).padStart(2, '0')}h UTC`;
    }
  }
  return label;
}

const dayMilliseconds = 24 * 60 * 60 * 1000;

function parseUtcDashboardDate(value: string) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return null;
  const [, year, month, day] = match;
  if (!year || !month || !day) return null;
  const date = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)));
  return Number.isNaN(date.getTime()) || date.toISOString().slice(0, 10) !== value ? null : date;
}

function formatPreviousDateRange(from: Date, to: Date, currentYear: number) {
  const sameDay = from.getTime() === to.getTime();
  const sameMonth =
    from.getUTCFullYear() === to.getUTCFullYear() && from.getUTCMonth() === to.getUTCMonth();
  const includeYear = from.getUTCFullYear() !== currentYear || to.getUTCFullYear() !== currentYear;
  const monthDay = dateTimeFormat({ month: 'short', day: 'numeric', timeZone: 'UTC' });

  if (sameDay) {
    return `${monthDay.format(from)}${includeYear ? `, ${String(from.getUTCFullYear())}` : ''}`;
  }
  if (sameMonth) {
    return `${monthDay.format(from)}–${String(to.getUTCDate())}${includeYear ? `, ${String(to.getUTCFullYear())}` : ''}`;
  }
  if (from.getUTCFullYear() === to.getUTCFullYear()) {
    return `${monthDay.format(from)}–${monthDay.format(to)}${includeYear ? `, ${String(to.getUTCFullYear())}` : ''}`;
  }
  return `${monthDay.format(from)}, ${String(from.getUTCFullYear())}–${monthDay.format(to)}, ${String(to.getUTCFullYear())}`;
}
